import { useMemo, useRef, useState } from 'react';
import { isValidLine } from './Shapes/Line';
import { isValidPolygon } from './Shapes/Polygon';
import { type DrawingCanvasShape, type DrawingCanvasShapeKey, DrawingCanvasShapeType, type Point } from './types';
import {
  arrowKeySteps,
  getNearestPointOnLine,
  getSelectedPartKey,
  getSelectedShapeFeatures,
  insertVertex,
  isUnmodifiedKey,
  moveVertex,
  removeVertex,
} from './utils';

/** How far the pointer must move from the mousedown start point before a vertex starts dragging. */
const dragThreshold = 3;

type UseEditingProps = {
  selectedShape: DrawingCanvasShape | undefined;
  onShapeChange?: (shape: DrawingCanvasShape) => void;
  onShapeDelete: (key: DrawingCanvasShapeKey) => void;
};

/** State and handlers for editing the selected shape's vertices. */
export const useEditing = ({ selectedShape, onShapeChange, onShapeDelete }: UseEditingProps) => {
  const isPointerDownOnShapeRef = useRef(false);
  const pointerDownPointRef = useRef<Point | undefined>(undefined);
  const isDraggingRef = useRef(false);
  const [selectedFeatureKey, setSelectedFeatureKey] = useState<DrawingCanvasShapeKey | undefined>(undefined);
  const [editedSelectedShapePoints, setEditedSelectedShapePoints] = useState<Point[] | undefined>(undefined);
  const [newVertexPoint, setNewVertexPoint] = useState<Point | undefined>(undefined);
  const [hoveredKey, setHoveredKey] = useState<DrawingCanvasShapeKey | undefined>(undefined);
  const [previousSelectedShapeKey, setPreviousSelectedShapeKey] = useState(selectedShape?.key);

  // Drops the selected feature, drag and hover preview when the selection changes
  if (previousSelectedShapeKey !== selectedShape?.key) {
    setPreviousSelectedShapeKey(selectedShape?.key);
    setSelectedFeatureKey(undefined);
    setEditedSelectedShapePoints(undefined);
    setNewVertexPoint(undefined);
    setHoveredKey(undefined);
  }

  const selectedShapeWithEdits = useMemo(
    () => selectedShape && { ...selectedShape, points: editedSelectedShapePoints ?? selectedShape.points },
    [selectedShape, editedSelectedShapePoints],
  );

  const { bBoxFeatures, vertexFeatures } = useMemo(
    () =>
      selectedShapeWithEdits
        ? getSelectedShapeFeatures(selectedShapeWithEdits, selectedFeatureKey, hoveredKey)
        : { bBoxFeatures: {}, vertexFeatures: {} },
    [selectedShapeWithEdits, selectedFeatureKey, hoveredKey],
  );

  const selectedFeature = selectedFeatureKey
    ? (bBoxFeatures[selectedFeatureKey] ?? vertexFeatures[selectedFeatureKey])
    : undefined;

  const getNearestPointOnSelectedShape = (shapeKey: DrawingCanvasShapeKey | undefined, point: Point) => {
    if (!selectedShape || shapeKey !== selectedShape.key) return undefined;
    if (selectedShape.type === DrawingCanvasShapeType.LINE) return getNearestPointOnLine(selectedShape.points, point);
    if (selectedShape.type === DrawingCanvasShapeType.POLYGON)
      return getNearestPointOnLine([...selectedShape.points, selectedShape.points[0]], point);
    return undefined;
  };

  const handlePointerDown = (point: Point, clickedShapeKey: DrawingCanvasShapeKey | undefined) => {
    setNewVertexPoint(undefined);
    isPointerDownOnShapeRef.current = clickedShapeKey !== undefined;
    pointerDownPointRef.current = point;
    isDraggingRef.current = false;
    if (!clickedShapeKey) return;
    const nearestPointOnSelectedShape = getNearestPointOnSelectedShape(clickedShapeKey, point);
    if (selectedShape && nearestPointOnSelectedShape) {
      const { segmentIndex, point: newVertex } = nearestPointOnSelectedShape;
      setEditedSelectedShapePoints(insertVertex(selectedShape.points, segmentIndex, newVertex));
      setSelectedFeatureKey(getSelectedPartKey(selectedShape.key, 'vertex', segmentIndex + 1));
    } else if (clickedShapeKey in bBoxFeatures || clickedShapeKey in vertexFeatures) {
      setSelectedFeatureKey(clickedShapeKey);
    } else {
      setSelectedFeatureKey(undefined);
    }
  };

  const handlePointerMove = (point: Point, hoveredShapeKey: DrawingCanvasShapeKey | undefined) => {
    if (!isPointerDownOnShapeRef.current) {
      // Unselected shapes and selected vertices highlight; the selected outline previews a new vertex instead
      const isHighlightable =
        hoveredShapeKey !== undefined && hoveredShapeKey !== selectedShape?.key && !(hoveredShapeKey in bBoxFeatures);
      setHoveredKey(isHighlightable ? hoveredShapeKey : undefined);
      setNewVertexPoint(getNearestPointOnSelectedShape(hoveredShapeKey, point)?.point);
      return;
    }
    if (
      (selectedShape?.type !== DrawingCanvasShapeType.LINE && selectedShape?.type !== DrawingCanvasShapeType.POLYGON) ||
      selectedFeature?.type !== DrawingCanvasShapeType.VERTEX
    )
      return;
    if (!isDraggingRef.current) {
      const pointerDownPoint = pointerDownPointRef.current ?? point;
      if (Math.hypot(point.x - pointerDownPoint.x, point.y - pointerDownPoint.y) < dragThreshold) return;
      isDraggingRef.current = true;
    }
    setEditedSelectedShapePoints(
      moveVertex(editedSelectedShapePoints ?? selectedShape.points, selectedFeature.index, point),
    );
  };

  const handlePointerUp = () => {
    isPointerDownOnShapeRef.current = false;
    if (editedSelectedShapePoints && selectedShape) {
      onShapeChange?.({ ...selectedShape, points: editedSelectedShapePoints });
    }
    setEditedSelectedShapePoints(undefined);
  };

  const handlePointerCancel = () => {
    isPointerDownOnShapeRef.current = false;
    setEditedSelectedShapePoints(undefined);
  };

  const handlePointerLeave = () => {
    setNewVertexPoint(undefined);
    setHoveredKey(undefined);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (
      (selectedShape?.type !== DrawingCanvasShapeType.LINE && selectedShape?.type !== DrawingCanvasShapeType.POLYGON) ||
      selectedFeature?.type !== DrawingCanvasShapeType.VERTEX
    ) {
      return;
    }
    if (!isUnmodifiedKey(event)) return;

    const vertexIndex = selectedFeature.index;
    const step = arrowKeySteps[event.key];
    if (step) {
      event.preventDefault();
      const vertex = selectedShape.points[vertexIndex];
      const points = moveVertex(selectedShape.points, vertexIndex, { x: vertex.x + step.x, y: vertex.y + step.y });
      onShapeChange?.({ ...selectedShape, points });
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      const updatedShape = { ...selectedShape, points: removeVertex(selectedShape.points, vertexIndex) };
      if (isValidLine(updatedShape) || isValidPolygon(updatedShape)) {
        onShapeChange?.(updatedShape);
      } else {
        onShapeDelete(selectedShape.key);
      }
      setSelectedFeatureKey(undefined);
      setHoveredKey(undefined);
    }
  };

  return {
    selectedShapeWithEdits,
    bBoxFeatures,
    vertexFeatures,
    newVertexPoint,
    hoveredKey,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    handlePointerLeave,
    handleKeyDown,
  };
};
